import React from 'react';
import { NavLink, useRouteMatch } from 'react-router-dom';
import { ChevronRight } from 'react-feather';
import { useSidebar } from './SidebarContext';

// Closes the mobile drawer after the user taps a leaf link.
const useCloseOnMobile = () => {
  const { setCollapsed } = useSidebar();
  return () => {
    if (typeof window !== 'undefined' && window.innerWidth <= 991) {
      setCollapsed(true);
    }
  };
};

function LeafLink({ item, depth, disabled }) {
  const onClick = useCloseOnMobile();
  const Icon = item.icon;
  const className = depth === 0 ? 'cb-item-row' : 'cb-child-row';

  if (disabled) {
    return (
      <span className={`${className} is-disabled`}>
        {(depth === 0 && Icon) && <span className="cb-item-icon"><Icon /></span>}
        <span className={depth === 0 ? 'cb-item-label' : ''}>{item.title}</span>
      </span>
    );
  }

  // Placeholder items with no path or '#' (e.g. "Needs to be added") render
  // inert, so NavLink doesn't crash on `to={undefined}` and so the user
  // doesn't navigate to '#'.
  const hasUsablePath = item.path && item.path !== '#';

  if (!hasUsablePath || item.type === 'external_link') {
    if (!item.title) return null;
    return (
      <a
        href={hasUsablePath ? item.path : undefined}
        target={item.type === 'external_link' && hasUsablePath ? '_blank' : undefined}
        rel="noreferrer"
        onClick={hasUsablePath ? onClick : (e) => e.preventDefault()}
        className={`${className}${!hasUsablePath ? ' is-disabled' : ''}`}
        style={!hasUsablePath ? { cursor: 'default' } : undefined}
      >
        {(depth === 0 && Icon) && <span className="cb-item-icon"><Icon /></span>}
        <span className={depth === 0 ? 'cb-item-label' : ''}>{item.title}</span>
      </a>
    );
  }

  return (
    <NavLink
      to={item.path}
      exact={false}
      activeClassName="is-active"
      className={className}
      onClick={onClick}
    >
      {(depth === 0 && Icon) && <span className="cb-item-icon"><Icon /></span>}
      <span className={depth === 0 ? 'cb-item-label' : ''}>{item.title}</span>
    </NavLink>
  );
}

// Recursively collect all leaf paths in a subtree so we can mark the parent
// as "active" if any descendant matches the URL. Skips nested subs and any
// item whose path is missing or just '#' (placeholder external links).
const collectLeafPaths = (item) => {
  if (!item) return [];
  if (Array.isArray(item.children)) {
    return item.children.flatMap(collectLeafPaths);
  }
  if (item.path && item.path !== '#') return [item.path];
  return [];
};

function SubItem({ item, depth, expandedSet, onToggleExpand, disabledTitles }) {
  const Icon = item.icon;
  const open = expandedSet.has(item.title);

  // Active highlight if any descendant matches the URL. We always call the
  // hook (rules of hooks) — using a never-matching sentinel when no leaf
  // paths are present so the hook returns null cleanly.
  const childPaths = collectLeafPaths(item);
  const isActive = !!useRouteMatch({
    path: childPaths.length ? childPaths : '__no_match__',
    exact: false,
  });

  return (
    <div className={`cb-item-wrap ${open ? 'is-open' : ''}`}>
      <button
        type="button"
        className={`cb-item-row ${isActive ? 'is-active' : ''}`}
        onClick={() => onToggleExpand(item.title)}
        aria-expanded={open}
      >
        {Icon && <span className="cb-item-icon"><Icon /></span>}
        <span className="cb-item-label">{item.title}</span>
        <span className={`cb-item-chevron ${open ? 'is-open' : ''}`}>
          <ChevronRight size={14} />
        </span>
      </button>

      {/* Inline accordion for expanded mode, flyout for collapsed mode.
          Same DOM, switched via CSS. Recurses through SidebarMenuItem so
          nested sub-items render as their own accordions. */}
      <ul className="cb-children">
        <li className="cb-flyout-title">{item.title}</li>
        {(item.children || []).map((child, idx) => (
          <li key={idx}>
            <SidebarMenuItem
              item={child}
              depth={depth + 1}
              expandedSet={expandedSet}
              onToggleExpand={onToggleExpand}
              disabledTitles={disabledTitles}
            />
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function SidebarMenuItem({
  item,
  depth = 0,
  expandedSet,
  onToggleExpand,
  disabledTitles = [],
}) {
  if (item.type === 'sub') {
    return (
      <SubItem
        item={item}
        depth={depth}
        expandedSet={expandedSet}
        onToggleExpand={onToggleExpand}
        disabledTitles={disabledTitles}
      />
    );
  }

  const disabled = disabledTitles.includes(item.title);

  // Top-level leaf items get tooltip support in collapsed mode; we wrap them
  // in cb-item-wrap so the ::after-style hover zone behaves predictably.
  if (depth === 0) {
    return (
      <div className="cb-item-wrap">
        <LeafLink item={item} depth={depth} disabled={disabled} />
        <span className="cb-tooltip">{item.title}</span>
      </div>
    );
  }
  return <LeafLink item={item} depth={depth} disabled={disabled} />;
}
